import { Checkbox, FormControlLabel, FormGroup, Stack, TextField } from '@mui/material';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { RealityInput, useRealitiesDataQuery } from '../../../../../../../gql';

interface Props {
  stepIndex: number;
  activeStep: number;
  onStepComplete: () => void;
}

export const ReferralStep: React.FC<Props> = () => {
  const realitiesQuery = useRealitiesDataQuery();
  const { formatMessage } = useIntl();

  const { control, getValues } = useFormContext<RealityInput>();
  const referralIds = useWatch({ control, name: 'referralIds' });

  // Find the "Other" option (system option)
  const otherOption = realitiesQuery.data?.referrals.find((r) => r.isSystemOption);
  const isOtherSelected = otherOption && referralIds?.includes(otherOption.id);

  return (
    <Stack spacing={2}>
      <Controller
        name="referralIds"
        control={control}
        rules={{
          validate: (referralIds) =>
            getValues('accompanimentIds').length === 0
              && getValues('approachIds').length === 0
              && referralIds.length === 0
              ? formatMessage({ id: 'A reality must contain at least one approach, one accompaniment or one referral.' })
              : true
        }}
        render={({ field }) => (
          <FormGroup>
            {realitiesQuery.data?.referrals.map((referral, index) => (
              <FormControlLabel
                key={referral.id}
                name={`${field.name}[${index}]`}
                control={<Checkbox checked={field.value && field.value.includes(referral.id)} />}
                onChange={(e, checked) => {
                  if (checked) {
                    field.onChange([...field.value, referral.id]);
                  } else {
                    field.onChange(
                      field.value.filter((item) => item !== referral.id)
                    );
                  }
                }}
                label={referral.name} />
            ))}
          </FormGroup>
        )}
      />

      {isOtherSelected && (
        <Controller
          name="referralOtherValue"
          control={control}
          rules={{ required: formatMessage({ id: 'Please specify the other referral' }) }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={formatMessage({ id: 'Please specify' })}
              placeholder={formatMessage({ id: 'Describe the other referral' })}
              fullWidth
              required
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      )}
    </Stack>
  );
};